import React, { FC, useCallback, useState } from 'react'
import { History } from 'history'
import { Box, Grid, makeStyles, Switch, Typography } from '@material-ui/core'
import SaveAltOutlinedIcon from '@material-ui/icons/SaveAltOutlined'
import EditIcon from '@material-ui/icons/Edit'

import PageHeader from 'App/components/PageHeader'
import { SUPER_ADMIN } from 'civic-champs-shared/auth/utils/permissions'
import { ReadOnlyQuestionSetQuestions } from 'civic-champs-shared/question-sets/components/'
import { ContainedButton, Loading, TableMenuButton } from 'civic-champs-shared/core/'
import { useEditQuestionSet, useQuestionSet } from 'civic-champs-shared/question-sets/hooks'
import { QuestionSetResponse } from 'civic-champs-shared/question-sets/types'

import { useHasRole } from 'auth/hooks'
import { useDownloadExportedQuestionnaire, useInactiveQuestionSetPrompt } from '../hooks'
import { useShowShowExportAnswersPrompt } from '../hooks/useShowExportAnswersPrompt'

const useStyles = makeStyles(() => ({
  actionsBar: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: 24,
  },
  headerButton: {
    margin: 5,
    fontSize: 14,
  },
  toggle: {
    marginTop: 5,
  },
  toggleLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    margin: 5,
  },
  statusLabel: {
    fontWeight: 'bold',
    marginRight: 5,
  },
}))

export interface QuestionnaireDetailsProps {
  match: {
    params: { id: string }
  }
  history: History
  backLink?: string
  backText?: string
}

export const QuestionnaireDetails: FC<QuestionnaireDetailsProps> = ({
  match,
  backLink = '/questionnaires',
  backText = 'Back to Questionnaires',
  history,
}) => {
  const questionSetId = parseInt(match.params.id)
  const [questionSet, setQuestionSet] = useState<QuestionSetResponse | null>(null)
  const { loading } = useQuestionSet(questionSetId, setQuestionSet)
  const classes = useStyles()
  const [downloadExportedQuestionnaire] = useDownloadExportedQuestionnaire()
  const [editQuestionSet, { loading: editLoading }] = useEditQuestionSet(setQuestionSet, false)
  const isSuperAdmin = useHasRole(SUPER_ADMIN)
  const inactiveQuestionSetPrompt = useInactiveQuestionSetPrompt()
  const download = useCallback(
    (value: { startDate?: string; endDate?: string }) => {
      ;(downloadExportedQuestionnaire as any)(questionSet, value)
    },
    [downloadExportedQuestionnaire, questionSet],
  )

  const showExportPrompt = useShowShowExportAnswersPrompt(download)
  const setActive = (active: boolean) =>
    questionSet && editQuestionSet({ ...questionSet, anonymous: !active, questions: undefined })

  const onToggleActive = async (active: boolean) => {
    if (active) {
      setActive(true)
    } else {
      inactiveQuestionSetPrompt(() => setActive(false))
    }
  }

  if (loading || editLoading) {
    return <Loading />
  }

  const { anonymous, name, questions } = questionSet || {}

  return (
    <PageHeader title="View Questionnaire" subTitle={backText} backLink={backLink} isolateContentTheme>
      <div className={classes.actionsBar}>
        {isSuperAdmin && (
          <>
            <Typography className={classes.toggleLabel} component="span">
              <Box fontWeight={'bold'} className={classes.statusLabel} component="span">
                Status:
              </Box>
              {anonymous ? 'Inactive' : 'Active'}
            </Typography>

            <Switch className={classes.toggle} checked={!anonymous} onChange={e => onToggleActive(e.target.checked)} />
          </>
        )}
        <TableMenuButton
          className={classes.headerButton}
          startIcon={<SaveAltOutlinedIcon />}
          onClick={showExportPrompt}
        >
          Export Responses
        </TableMenuButton>
        <ContainedButton
          startIcon={<EditIcon />}
          className={classes.headerButton}
          onClick={() => history.push(`/questionnaires/${questionSetId}/edit`)}
        >
          Edit
        </ContainedButton>
      </div>

      <Grid container spacing={2} direction="column">
        <Grid item>
          <Typography variant="h5">{name || '-'}</Typography>
        </Grid>
        <Grid item>
          <ReadOnlyQuestionSetQuestions questions={questions || []} />
        </Grid>
      </Grid>
    </PageHeader>
  )
}
